import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, Ticket, Sparkle, WarningCircle, CheckCircle, MapPin } from "@phosphor-icons/react";
import { travelDateApi } from "@/lib/apiClient";
import { useAuth } from "@/context/AuthContext";
import { PassportLogo } from "@/components/Reveal";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";

const GOLD  = "#D4AF37";
const CORAL = "#F4A28F";

export default function EventsAdmin() {
    const { t } = useTranslation();
    const { user, loading } = useAuth();
    const navigate = useNavigate();
    const [cfg, setCfg] = useState(null);
    const [busy, setBusy] = useState(null);
    const [preview, setPreview] = useState([]);
    const [previewQ, setPreviewQ] = useState("Dallas, Texas");
    const [previewBusy, setPreviewBusy] = useState(false);

    useEffect(() => {
        if (loading) return;
        if (!user) { navigate("/auth"); return; }
        if (!user.is_admin) { toast.error(t("eventsAdmin.toast.adminOnly")); navigate("/app"); return; }
        travelDateApi.eventsConfig().then(setCfg).catch(() => toast.error(t("eventsAdmin.toast.loadConfigError")));
    }, [loading, user, navigate, t]);

    const toggle = async (provider, next) => {
        setBusy(provider);
        try {
            const updated = await travelDateApi.setEventsConfig(provider, next);
            setCfg(updated);
            const status = next ? t("eventsAdmin.status.enabled") : t("eventsAdmin.status.disabled");
            toast.success(t("eventsAdmin.toast.toggleSuccess", { provider, status }));
        } catch (e) {
            const raw = e?.response?.data?.detail;
            const msg = typeof raw === "string" ? raw : Array.isArray(raw) ? raw.map(x => x.msg).join(", ") : t("eventsAdmin.toast.updateFailed");
            toast.error(msg);
        } finally { setBusy(null); }
    };

    const runPreview = async () => {
        if (!previewQ.trim()) return;
        setPreviewBusy(true);
        try {
            const r = await travelDateApi.eventsPreview(previewQ.trim(), "romantic");
            setPreview(r.events || []);
        } catch (e) { toast.error(t("eventsAdmin.toast.previewFailed")); }
        finally { setPreviewBusy(false); }
    };

    if (loading || !user || !cfg) return <div className="min-h-screen grid place-items-center text-white/60">{t("common.loading")}</div>;

    return (
        <div className="min-h-screen grain pb-16" data-testid="events-admin-page">
            {/* Header */}
            <header className="sticky top-0 z-40 glass border-b" style={{ borderColor: "rgba(212,175,55,0.2)" }}>
                <div className="max-w-[1100px] mx-auto px-5 sm:px-8 md:px-10 py-4 flex items-center justify-between">
                    <Link to="/owner" className="flex items-center gap-3">
                        <ArrowLeft size={16} color={GOLD} />
                        <PassportLogo size={26} />
                        <div className="hidden sm:block leading-none">
                            <div className="font-serif text-base text-white">{t("conciergeApp.passport")}</div>
                            <div className="text-[9px] tracking-[0.3em] uppercase" style={{ color: GOLD }}>{t("eventsAdmin.headerSub")}</div>
                        </div>
                    </Link>
                </div>
            </header>

            <div className="max-w-[860px] mx-auto px-5 sm:px-8 md:px-10 pt-12">
                <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}>
                    <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full mb-4" style={{ background: "rgba(212,175,55,0.14)", border: `1px solid ${GOLD}55` }}>
                        <Ticket size={11} weight="fill" color={GOLD} />
                        <span className="text-[10px] tracking-[0.3em] uppercase" style={{ color: GOLD }}>{t("eventsAdmin.heroOverline")}</span>
                    </div>
                    <h1 className="font-serif text-4xl sm:text-5xl text-white leading-tight tracking-tight">
                        {t("eventsAdmin.heroTitle")}
                    </h1>
                    <p className="text-white/60 text-sm mt-3 max-w-xl">
                        {t("eventsAdmin.heroDesc")}
                    </p>
                </motion.div>

                <div className="mt-10 space-y-3">
                    <ProviderRow
                        keyName="ai"
                        title={t("eventsAdmin.ai.title")}
                        subtitle={t("eventsAdmin.ai.subtitle")}
                        icon={<Sparkle size={18} weight="fill" color={GOLD} />}
                        enabled={cfg.ai?.enabled}
                        onToggle={(v) => toggle("ai", v)}
                        busy={busy === "ai"}
                    />
                    <ProviderRow
                        keyName="ticketmaster"
                        title={t("eventsAdmin.ticketmaster.title")}
                        subtitle={
                            cfg.ticketmaster?.has_key
                                ? t("eventsAdmin.ticketmaster.hasKeySub")
                                : t("eventsAdmin.ticketmaster.noKeySub")
                        }
                        icon={<Ticket size={18} weight="fill" color={GOLD} />}
                        enabled={cfg.ticketmaster?.enabled}
                        onToggle={(v) => toggle("ticketmaster", v)}
                        busy={busy === "ticketmaster"}
                        badge={
                            cfg.ticketmaster?.has_key
                                ? <span className="text-[9px] tracking-[0.25em] uppercase inline-flex items-center gap-1" style={{ color: "#9FD5D0" }}><CheckCircle size={10} weight="fill" /> {t("eventsAdmin.ticketmaster.keyDetected")}</span>
                                : <span className="text-[9px] tracking-[0.25em] uppercase inline-flex items-center gap-1" style={{ color: CORAL }}><WarningCircle size={10} weight="fill" /> {t("eventsAdmin.ticketmaster.noApiKey")}</span>
                        }
                        disabled={!cfg.ticketmaster?.has_key}
                        disabledReason={cfg.ticketmaster?.disabled_reason}
                    />
                </div>

                {/* Preview */}
                <div className="mt-14 rounded-3xl p-6" style={{ background: "var(--navy-deep)", border: `1px solid ${GOLD}22` }} data-testid="events-preview-card">
                    <div className="text-[10px] tracking-[0.3em] uppercase text-white/40 mb-2">{t("eventsAdmin.preview.overline")}</div>
                    <div className="font-serif text-2xl text-white mb-4">{t("eventsAdmin.preview.title")}</div>
                    <div className="flex gap-2">
                        <input
                            value={previewQ}
                            onChange={(e) => setPreviewQ(e.target.value)}
                            placeholder={t("eventsAdmin.preview.placeholder")}
                            className="flex-1 bg-transparent text-white text-sm px-4 py-2.5 rounded-full focus:outline-none placeholder:text-white/40"
                            style={{ background: "rgba(0,0,0,0.35)", border: `1px solid ${GOLD}33` }}
                            data-testid="events-preview-input"
                        />
                        <button onClick={runPreview} disabled={previewBusy} className="text-xs px-4 rounded-full font-semibold disabled:opacity-50" style={{ background: GOLD, color: "var(--navy)" }} data-testid="events-preview-btn">
                            {previewBusy ? t("eventsAdmin.preview.fetching") : t("eventsAdmin.preview.button")}
                        </button>
                    </div>
                    <div className="mt-4 space-y-2">
                        {preview.length === 0 && !previewBusy && <div className="text-white/50 text-sm">{t("eventsAdmin.preview.empty")}</div>}
                        {preview.map((e) => (
                            <div key={e.id} className="rounded-xl p-3" style={{ background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.06)" }} data-testid="events-preview-item">
                                <div className="flex items-start justify-between gap-2">
                                    <div className="min-w-0">
                                        <div className="text-[9px] tracking-[0.25em] uppercase" style={{ color: e.source === "ticketmaster" ? "#9FD5D0" : GOLD }}>
                                            {e.source === "ticketmaster" ? t("eventsAdmin.preview.verified") : t("eventsAdmin.preview.unverified")}
                                        </div>
                                        <div className="text-white text-sm mt-1">{e.name}</div>
                                        <div className="text-white/50 text-[11px] mt-0.5 inline-flex items-center gap-1">
                                            {e.venue && <><MapPin size={10} /> {e.venue} · </>}
                                            {e.date_iso || e.date_hint}
                                        </div>
                                    </div>
                                    {e.price_display && <div className="text-[10px] tracking-[0.2em] uppercase text-white/60 shrink-0">{e.price_display}</div>}
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        </div>
    );
}


function ProviderRow({ keyName, title, subtitle, icon, enabled, onToggle, busy, badge, disabled, disabledReason }) {
    return (
        <div
            className="rounded-2xl p-5 flex items-start gap-4"
            style={{ background: "var(--navy-deep)", border: `1px solid ${enabled ? GOLD + "55" : "rgba(255,255,255,0.06)"}` }}
            data-testid={`events-provider-${keyName}`}
        >
            <div className="w-10 h-10 rounded-2xl grid place-items-center shrink-0" style={{ background: "rgba(212,175,55,0.12)", border: `1px solid ${GOLD}44` }}>
                {icon}
            </div>
            <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                    <div className="text-white font-medium">{title}</div>
                    {badge}
                </div>
                <div className="text-white/60 text-[12.5px] mt-1 leading-relaxed">{subtitle}</div>
                {disabled && disabledReason && (
                    <div className="text-[11px] mt-2" style={{ color: CORAL }}>{disabledReason}</div>
                )}
            </div>
            <button
                onClick={() => !disabled && !busy && onToggle(!enabled)}
                disabled={disabled || busy}
                className="shrink-0 relative w-12 h-6 rounded-full transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                style={{ background: enabled ? GOLD : "rgba(255,255,255,0.14)" }}
                data-testid={`events-toggle-${keyName}`}
            >
                <span className="absolute top-0.5 left-0.5 w-5 h-5 rounded-full bg-white transition-transform" style={{ transform: enabled ? "translateX(24px)" : "translateX(0)" }} />
            </button>
        </div>
    );
}
